import {
  CLEF_INFO,
  describePosition,
  landmarkPosition,
  pitchAtPosition,
  staffPosition,
  staffLineNames,
  staffSpaceNames,
  isLinePosition,
  STAFF_BOTTOM_LINE,
  STAFF_TOP_LINE,
  type Clef,
} from '../../clef.ts';
import {
  ACCIDENTAL_ASCII,
  ACCIDENTAL_SYMBOL,
  LETTERS,
  isAccidental,
  isLetter,
  letterIndex,
  midiNumber,
  parsePitchId,
  pitchLabel,
  type Accidental,
  type Letter,
  type SpelledPitch,
} from '../../pitch.ts';
import type { Feedback, GradeResult, Hint } from '../types.ts';
import type { AnswerMode } from './config.ts';
import type { NoteIdExpected, NoteIdPrompt } from './generate.ts';

export interface NoteIdAnswer {
  letter: Letter;
  accidental: Accidental;
  /** Required only for the spelled-note-with-octave answer mode. */
  octave: number | null;
}

export const NOTE_ID_GRADE_CODES = [
  'correct',
  'enharmonic',
  'wrong-accidental',
  'wrong-octave',
  'clef-confusion',
  'step-off',
  'wrong-letter',
] as const;
export type NoteIdGradeCode = (typeof NOTE_ID_GRADE_CODES)[number];

export const GRADE_CODE_LABEL: Record<NoteIdGradeCode, string> = {
  correct: 'Correct',
  enharmonic: 'Enharmonic spelling',
  'wrong-accidental': 'Accidental missed',
  'wrong-octave': 'Wrong octave',
  'clef-confusion': 'Read in another clef',
  'step-off': 'One step off',
  'wrong-letter': 'Other letter',
};

/**
 * Strict answer parsing per answer mode. Letter mode accepts only naturals;
 * octave mode requires an octave. Returns null for malformed input.
 */
export function parseNoteIdAnswer(input: unknown, mode: AnswerMode): NoteIdAnswer | null {
  if (typeof input !== 'object' || input === null) return null;
  const raw = input as Record<string, unknown>;
  if (typeof raw.letter !== 'string' || !isLetter(raw.letter)) return null;
  const accidental = raw.accidental === undefined || raw.accidental === null ? 'natural' : raw.accidental;
  if (typeof accidental !== 'string' || !isAccidental(accidental)) return null;
  if (mode === 'letter' && accidental !== 'natural') return null;
  let octave: number | null = null;
  if (mode === 'spelled-octave') {
    if (typeof raw.octave !== 'number' || !Number.isInteger(raw.octave) || raw.octave < 0 || raw.octave > 8) return null;
    octave = raw.octave;
  }
  return { letter: raw.letter, accidental, octave };
}

/** Canonical answer text stored with events, e.g. "C", "C#", "C#4". */
export function noteIdAnswerText(answer: NoteIdAnswer): string {
  return `${answer.letter}${ACCIDENTAL_ASCII[answer.accidental]}${answer.octave ?? ''}`;
}

export function noteIdAnswerLabel(answer: NoteIdAnswer): string {
  return `${answer.letter}${ACCIDENTAL_SYMBOL[answer.accidental]}${answer.octave ?? ''}`;
}

/** The answer that is correct for an expected pitch under its answer mode. */
export function expectedAnswer(expected: NoteIdExpected): NoteIdAnswer {
  const target = parsePitchId(expected.pitch) as SpelledPitch;
  return {
    letter: target.letter,
    accidental: expected.answerMode === 'letter' ? 'natural' : target.accidental,
    octave: expected.answerMode === 'spelled-octave' ? target.octave : null,
  };
}

function isCorrect(target: SpelledPitch, answer: NoteIdAnswer, mode: AnswerMode): boolean {
  if (answer.letter !== target.letter) return false;
  if (mode === 'letter') return true;
  if (answer.accidental !== target.accidental) return false;
  if (mode === 'spelled') return true;
  return answer.octave === target.octave;
}

function soundsSame(target: SpelledPitch, answer: NoteIdAnswer, mode: AnswerMode): boolean {
  if (mode === 'letter') return false;
  const answerPitch: SpelledPitch = { letter: answer.letter, accidental: answer.accidental, octave: answer.octave ?? target.octave };
  if (mode === 'spelled-octave') return midiNumber(answerPitch) === midiNumber(target);
  // Without octaves compare pitch classes.
  const diff = (((midiNumber(answerPitch) - midiNumber(target)) % 12) + 12) % 12;
  return diff === 0;
}

/**
 * Clefs a reader of `clef` plausibly confuses it with: the treble/bass pair,
 * and the familiar clefs for alto and tenor readers.
 */
const CONFUSABLE_CLEFS: Record<Clef, Clef[]> = {
  treble: ['bass'],
  bass: ['treble'],
  alto: ['treble'],
  tenor: ['bass', 'treble'],
};

/** Other clef in which the answered letter would sit at this staff position, if any. */
export function confusedClef(clef: Clef, position: number, letter: Letter): Clef | null {
  for (const other of CONFUSABLE_CLEFS[clef]) {
    if (pitchAtPosition(other, position).letter === letter) return other;
  }
  return null;
}

export function gradeNoteId(expected: NoteIdExpected, answer: NoteIdAnswer): GradeResult<NoteIdGradeCode> {
  const clef = expected.clef;
  const target = parsePitchId(expected.pitch);
  if (!target) throw new Error(`Invalid expected pitch ${expected.pitch}`);
  const mode = expected.answerMode;
  if (isCorrect(target, answer, mode)) return { correct: true, code: 'correct' };
  if (answer.letter === target.letter) {
    if (mode !== 'letter' && answer.accidental !== target.accidental) return { correct: false, code: 'wrong-accidental' };
    return { correct: false, code: 'wrong-octave' };
  }
  if (soundsSame(target, answer, mode)) return { correct: false, code: 'enharmonic' };
  const position = staffPosition(clef, target);
  if (confusedClef(clef, position, answer.letter)) return { correct: false, code: 'clef-confusion' };
  const letterDistance = Math.abs(((letterIndex(answer.letter) - letterIndex(target.letter) + 7) % 7));
  if (letterDistance === 1 || letterDistance === 6) return { correct: false, code: 'step-off' };
  return { correct: false, code: 'wrong-letter' };
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function letterSequence(from: Letter, steps: number): Letter[] {
  const out: Letter[] = [from];
  const dir = steps >= 0 ? 1 : -1;
  let index = letterIndex(from);
  for (let i = 0; i < Math.abs(steps); i++) {
    index = (index + dir + 7) % 7;
    out.push(LETTERS[index] as Letter);
  }
  return out;
}

/** Best landmark for counting: the clef's own line, or the nearest outer staff line. */
function nearestLandmark(clef: Clef, position: number): { position: number; label: string; describe: string } {
  const candidates = [
    { position: landmarkPosition(clef), describe: CLEF_INFO[clef].landmark.description },
    {
      position: STAFF_BOTTOM_LINE,
      describe: `The bottom line of the ${clef} staff is ${pitchLabel(pitchAtPosition(clef, STAFF_BOTTOM_LINE))}.`,
    },
    {
      position: STAFF_TOP_LINE,
      describe: `The top line of the ${clef} staff is ${pitchLabel(pitchAtPosition(clef, STAFF_TOP_LINE))}.`,
    },
  ];
  let best = candidates[0]!;
  for (const c of candidates) {
    if (Math.abs(c.position - position) < Math.abs(best.position - position)) best = c;
  }
  return { position: best.position, label: pitchLabel(pitchAtPosition(clef, best.position)), describe: best.describe };
}

export function noteIdHint(prompt: NoteIdPrompt, expected: NoteIdExpected, level: number): Hint | null {
  const target = parsePitchId(expected.pitch);
  if (!target) return null;
  const position = staffPosition(prompt.clef, target);
  const d = describePosition(position);
  const accidentalNote =
    expected.answerMode !== 'letter' && target.accidental !== 'natural'
      ? ` Name the letter first, then add the ${target.accidental === 'sharp' ? 'sharp (♯)' : 'flat (♭)'} written in front of the note.`
      : '';

  if (level === 1) {
    const where = d.kind === 'space' ? 'in' : 'on';
    const group =
      d.region === 'staff' && position >= STAFF_BOTTOM_LINE && position <= STAFF_TOP_LINE
        ? d.kind === 'line'
          ? ` The ${prompt.clef} staff lines, bottom to top, are ${staffLineNames(prompt.clef).map((x) => x.letter).join(' ')}.`
          : ` The ${prompt.clef} staff spaces, bottom to top, are ${staffSpaceNames(prompt.clef).map((x) => x.letter).join(' ')}.`
        : '';
    return { level, text: `This note sits ${where} ${d.text}.${group}${accidentalNote}` };
  }
  if (level === 2) {
    const landmark = nearestLandmark(prompt.clef, position);
    const steps = position - landmark.position;
    if (steps === 0) {
      return { level, text: `${landmark.describe} This note is right on that line.${accidentalNote}` };
    }
    const direction = steps > 0 ? 'up' : 'down';
    const sequence = letterSequence(landmark.label.charAt(0) as Letter, steps);
    const shown = sequence.slice(0, -1).join(', ');
    return {
      level,
      text: `${landmark.describe} Count ${Math.abs(steps)} step${Math.abs(steps) === 1 ? '' : 's'} ${direction} (each line or space is one step): ${shown}, …${accidentalNote}`,
    };
  }
  return null;
}

export interface FeedbackOptions {
  /**
   * Whether the explanation may name the correct answer. The player withholds it
   * on a first miss (so the retry is still a real attempt) and reveals it after.
   */
  reveal?: boolean;
}

export function noteIdFeedback(
  prompt: NoteIdPrompt,
  expected: NoteIdExpected,
  answer: NoteIdAnswer,
  result: GradeResult<NoteIdGradeCode>,
  options: FeedbackOptions = {},
): Feedback {
  const reveal = options.reveal ?? true;
  const target = parsePitchId(expected.pitch) as SpelledPitch;
  const position = staffPosition(prompt.clef, target);
  const d = describePosition(position);
  const where = d.kind === 'space' ? 'in' : 'on';
  const correctLabel = noteIdAnswerLabel(expectedAnswer(expected));
  const answered = noteIdAnswerLabel(answer);
  const base = `${capitalize(where)} ${d.text} in ${prompt.clef} clef, the note is ${correctLabel}.`;
  const nudge = `It sits ${where} ${d.text}. ${CLEF_INFO[prompt.clef].landmark.description} Count from there.`;

  if (result.correct) {
    return { title: 'Correct', body: `${correctLabel} — ${where} ${d.text}.` };
  }
  switch (result.code) {
    case 'enharmonic':
      return {
        title: 'Same sound, different spelling',
        body: reveal
          ? `${answered} sounds the same as ${correctLabel}, but this note is written on the ${target.letter} ${isLinePosition(position) ? 'line' : 'space'} ${target.accidental === 'natural' ? 'with no sharp or flat' : target.accidental === 'sharp' ? 'with a sharp' : 'with a flat'}, so it is spelled ${correctLabel}.`
          : `${answered} sounds the same, but notes are spelled by where they’re written. Name the letter of the line or space first, then add the sign you see.`,
      };
    case 'wrong-accidental':
      return {
        title: 'Right letter — check the sign',
        body: reveal
          ? target.accidental === 'natural'
            ? `You found the letter ${target.letter}. There is no sharp or flat in front of this note, so it is ${correctLabel}.`
            : `You found the letter ${target.letter}. The ${target.accidental === 'sharp' ? 'sharp (♯)' : 'flat (♭)'} in front of the note makes it ${correctLabel}.`
          : `You found the letter ${target.letter}. Look just to the left of the note head: is there a sharp, a flat, or no sign at all?`,
      };
    case 'wrong-octave':
      return {
        title: 'Right name, different octave',
        body: reveal
          ? `${base} Middle C is C4, and the octave number goes up at every C — so B3 is just below middle C.`
          : 'The letter is right — now check the octave. Find middle C in this clef and count from there; the octave number goes up at every C.',
      };
    case 'clef-confusion': {
      const other = confusedClef(prompt.clef, position, answer.letter) as Clef;
      return {
        title: `That’s the ${other} clef reading`,
        body: reveal ? `${answer.letter} is where this note would be in ${other} clef. ${base}` : `${answer.letter} is where this note would be in ${other} clef. This one is in ${prompt.clef} clef. ${nudge}`,
      };
    }
    case 'step-off':
      return {
        title: 'One step away',
        body: reveal
          ? `${answer.letter} is one step ${letterIndex(answer.letter) === (letterIndex(target.letter) + 1) % 7 ? 'higher' : 'lower'}. ${base} Lines and spaces alternate, so check whether the note is on a line or in a space.`
          : `Close — you’re one step away. Lines and spaces alternate: is the note on a line or in a space? ${nudge}`,
      };
    default:
      return { title: 'Not quite', body: reveal ? base : nudge };
  }
}
